/* global mp */

import omit from 'lodash/omit';
import moment from 'moment-timezone';
import queryString from 'query-string';

import {
  apiHost,
  getCachedStandaloneAccessToken,
  getProjectID,
  isStandalone,
  isVolt,
  getIsWorkspaceGlobal,
  getWorkspaceId,
} from './report/util';
import {MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT} from './report/constants';
import {parseUrlQueryParams} from './util';
import {apiClient} from './util/api-client';
import {installDevFetchCache} from './report/queries/utils/fetch-cache';
import {setFlag, incrementFlag} from './report/queries/legacy-django-api/user-flags';

// Mixpanel.com client ID with implicit authorization grant type
// see oauth2_provider_application table for full configuration
const MIXPANEL_CLIENT_ID = `LJzdnKoilgp50qtCl1yZXuwwzlv5CgP9ung4MN0x`;

const checkUrlOptions = (urlOptions, prefix) => {
  return Object.keys(urlOptions)
    .map((k) => new RegExp(`^${prefix}-(.+)`).exec(k))
    .filter(Boolean)
    .map((match) => match[1]);
};

export default class MPContext {
  projectID: number;
  isStandalone: boolean;
  isVolt: boolean;
  accessToken: string;
  urlOptions: {[key: string]: string};
  apiOrigin: string;
  blocking: {is_blocked_events: boolean; is_blocked_people: boolean};
  canUseCohorts: boolean;
  downloadViewMode: boolean;
  downloadTitle?: string;
  downloadDisplayPreferences?: any;
  featureGates: {max_data_history_days: number; restricted_reports: string[]};
  featureGateName: string;
  flags: {[key: string]: number};
  hasIntegratedArb: boolean;
  hasIntegratedEngage: boolean;
  isMemberOfCurrentProject: boolean;
  maxDataHistoryDaysUpgrade: number;
  /** whether to apply permissions at all. false = everything is allowed */
  permissionsEnabled: boolean;
  projectFeatureFlags: string[];
  projectTimezone: string;
  projectType: number;
  setupMixpanelUrl: string;
  timezones: string[];
  userEmail: string;
  userID: number;
  userName: string;
  userPermissions: string[];
  utcOffset: number;
  variants: string[];
  whitelists: Set<string>;
  appName: string;
  organizationID: number;
  projectCampaigns: any;
  status: string;
  workspaceId: number;
  isWorkspaceGlobal: boolean;
  freeCustomer: boolean;

  constructor({oauthToken = null, projectID = null} = {}) {
    this.projectID = parseInt(projectID || getProjectID(), 10);
    this.isStandalone = isStandalone();
    this.isVolt = isVolt();
    this.accessToken = oauthToken;
    this.workspaceId = parseInt(getWorkspaceId(), 10);
    this.isWorkspaceGlobal = getIsWorkspaceGlobal();

    if (this.isStandalone) {
      this.urlOptions = queryString.parse(window.location.search);
      this.apiOrigin = apiHost();

      this.blocking = {
        is_blocked_events: false,
        is_blocked_people: false,
      };
      this.canUseCohorts = true;
      this.freeCustomer = false;
      this.downloadViewMode = !!this.urlOptions.download;
      this.featureGates = {
        max_data_history_days:
          parseInt(this.urlOptions.max_data_history_days, 10) || MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT,
        restricted_reports: [],
      };
      this.featureGateName = `standalone`;
      this.flags = {};
      this.hasIntegratedArb = true;
      this.hasIntegratedEngage = true;
      this.isMemberOfCurrentProject = false; // to disable downloading png/pdf in standalone mode (as it won't work and is just confusing)
      this.maxDataHistoryDaysUpgrade = 365 * 5;
      this.permissionsEnabled = false;
      this.projectFeatureFlags = checkUrlOptions(this.urlOptions, `feature`);
      this.projectTimezone = moment.tz.guess();
      this.projectType = parseInt(this.urlOptions.project_type, 10);
      this.setupMixpanelUrl = `/report/${this.projectID}/setup/quick/`;
      this.timezones = [
        `US/Alaska`,
        `US/Arizona`,
        `US/Central`,
        `US/Eastern`,
        `US/Hawaii`,
        `US/Mountain`,
        `US/Pacific`,
      ]; // mostly for development
      this.userEmail = `dev.test@mixpanel.com`;
      this.userID = 1;
      this.userName = `Mr. Iron`;
      this.userPermissions = [];
      this.utcOffset = -new Date().getTimezoneOffset();
      this.variants = [];
      this.whitelists = new Set(checkUrlOptions(this.urlOptions, `whitelist`));

      if (`fetch_cache` in this.urlOptions) {
        // hydrate cache (default) - respond from cache if match, fetch in background and update cache for next time
        // simple cache - respond from cache if match, otherwise fetch from network and respond
        const hydrate = this.urlOptions.fetch_cache !== `simple`;
        installDevFetchCache(hydrate);
      }
    } else {
      const {globals} = mp.report;
      // Globals
      this.accessToken = globals.access_token;
      this.apiOrigin = window.location.origin;
      this.appName = globals.app_name;
      this.blocking = globals.blocking;
      this.freeCustomer = globals.project_account_events_plan && globals.project_account_events_plan.is_free === true;
      this.canUseCohorts =
        (globals.feature_gates && !globals.feature_gates.restricted_reports.includes(`cohorts`)) ||
        (globals.project_featureflags && globals.project_featureflags.includes(`cohorts`));
      this.downloadViewMode = globals.download_view_mode;
      if (this.downloadViewMode) {
        this.downloadTitle = globals.download_title;
        this.downloadDisplayPreferences = globals.download_display_preferences;
      }

      this.featureGates = globals.feature_gates;
      this.featureGateName = globals.feature_gate_name;
      this.flags = globals.flags;
      this.hasIntegratedArb = globals.has_integrated_arb;
      this.hasIntegratedEngage = globals.has_integrated_engage;
      this.isMemberOfCurrentProject = (function checkMemberStatus() {
        if (mp && mp.report && mp.report.globals) {
          return mp.report.globals.is_project_member;
        } else {
          return false;
        }
      })();
      this.maxDataHistoryDaysUpgrade = globals.max_data_history_days_upgrade;
      this.organizationID = Number(globals.organization_id);
      this.permissionsEnabled = globals.permissions_enabled;
      this.projectCampaigns = globals.project_campaigns;
      this.projectFeatureFlags = globals.project_featureflags;
      const projectTimezone = globals.timezones.find((tz) => tz[0] === globals.project_timezone);
      this.projectTimezone = (projectTimezone && projectTimezone[1]) || moment.tz.guess(); // fallback to browser timezone
      this.projectType = globals.project_type;
      this.setupMixpanelUrl = globals.setup_mixpanel_url;
      this.timezones = globals.timezones.map(([, name]) => name);
      this.utcOffset = globals.utc_offset;
      this.userEmail = globals.user_email;
      this.userID = Number(globals.user_id);
      this.userName = globals.user_name;
      this.userPermissions = globals.permissions;
      this.variants = mp.globals.variants;
      this.whitelists = new Set(globals.whitelists);
    }
  }

  async performAuth({mustFetchToken = true} = {}) {
    if (this.isStandalone) {
      const parsedHash = queryString.parse(location.hash);
      const oAuthState = parsedHash.state ? JSON.parse(decodeURIComponent(parsedHash.state)) : null;
      const oAuthRedirectAttempts = oAuthState ? oAuthState.redirectAttempts : 0;
      const accessToken = this.accessToken || parsedHash[`access_token`] || getCachedStandaloneAccessToken();

      if (!accessToken) {
        // localhost doesn't allow calling mixpanel.com/oauth/access_token
        // because that endpoint doesn't have localhost in Access-Control-Allow-Origin header
        if (this.isVolt && window.location.hostname !== `localhost`) {
          // Volt mode - ideally we don't need the token right on startup, since the
          // API client will lazy-fetch it if necessary. But some (most?) apps use the
          // token for non-standard API calls, so until those are cleaned up they block
          // on fetching the token on startup.
          if (mustFetchToken) {
            this.accessToken = await apiClient.getAccessToken();
          }
        } else if (oAuthRedirectAttempts < 3) {
          this.doOAuthRedirect(oAuthRedirectAttempts + 1);
        } else {
          throw new Error(`Could not get valid OAuth token.`);
        }
      } else {
        this.accessToken = accessToken;
        apiClient.token = accessToken;

        // replace the hash with originalHash or clear it completely
        if (oAuthState) {
          window.location.hash = oAuthState.originalHash || ``;
        }
      }
    }

    // so we can chain e.g const mpContext = new MPContext().performAuth();
    return this;
  }

  doOAuthRedirect(redirectAttempts) {
    const state: any = {redirectAttempts};
    if (window.location.hash) {
      state.originalHash = window.location.hash;
    }

    const redirectPath = window.location.origin + window.location.pathname;
    const urlQueryParams = omit(parseUrlQueryParams(), `state`);
    /* eslint-disable camelcase */
    const oAuthParams = {
      client_id: MIXPANEL_CLIENT_ID,
      redirect_uri: encodeURIComponent(`${redirectPath}?${queryString.stringify(urlQueryParams)}`),
      response_type: `token`,
      scope: [
        `annotations`,
        `bookmarks`,
        `cohorts`,
        `correlate`,
        `custom_properties`,
        `dashboard_reports`,
        `data_definitions`,
        `data_groups`,
        `datasets`,
        `engage`,
        `erf_experiments`,
        `events`,
        `experiments`,
        `flows`,
        `funnels`,
        `insights`,
        `integrations`,
        `jql`,
        `messages`,
        `nps_survey`,
        `projects`,
        `retention`,
        `segmentation`,
        `smart_hub_alerts`,
        `user_details`,
        `visual_journeys`,
      ].join(` `),
      state: encodeURIComponent(JSON.stringify(state)),
    };
    /* eslint-enable camelcase */
    this.status = `redirecting`;
    window.location.href = `${this.apiOrigin}/oauth/authorize?${queryString.stringify(oAuthParams, {encode: false})}`;

    // location.href will not immediately start loading the new page.
    // we mark that the page is redirecting so attachApp in init-app.ts can show a redirecting message
    // when the the initiaized iron app throws accessToken not found error
    window[`__isRedirecting`] = true;
  }

  hasProjectFeatureFlag(name: string) {
    return this.projectFeatureFlags.includes(name) || this.projectFeatureFlags.includes(`dev`);
  }

  hasFeatureGate(name: string) {
    return this.featureGates && this.featureGates[name];
  }

  isExplicitlyWhitelisted(whitelist: string) {
    return this.whitelists.has(whitelist);
  }

  isWhitelisted(whitelist: string) {
    return this.isExplicitlyWhitelisted(whitelist) || this.whitelists.has(`dev`);
  }

  hasPermission(permission: string) {
    return !this.permissionsEnabled || this.userPermissions.includes(permission);
  }

  getFlag(flag: string) {
    return this.flags[flag];
  }

  setFlag(flag: string) {
    if (!this.isStandalone) {
      setFlag(flag);
      this.flags[flag] = mp.report.globals.flags[flag];
    } else {
      this.flags[flag] = 1;
    }
  }

  incrementFlag(flag: string) {
    if (!this.isStandalone) {
      incrementFlag(flag);
      this.flags[flag] = mp.report.globals.flags[flag];
    } else {
      this.flags[flag] = (this.flags[flag] || 0) + 1;
    }
  }
}
